import axios from 'axios'
import { User } from '@/domain/User'
import { ChangePassword } from '@/domain/ChangePassword'

/**
 * ユーザ管理サービス
 */
export default class UsersService {
    /** インスタンス */
    private static _instance: UsersService

    /**
     * コンストラクタ
     */
    // eslint-disable-next-line
    private constructor() {}

    /**
     * インスタンスの取得
     */
    public static get instance() {
        if (!this._instance) {
            this._instance = new UsersService();
        }
        return this._instance;
    }

    /**
     * 新規作成
     */
    public async createNew(): Promise<User> {
        return await axios.get("/api/user/new")
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            // eslint-disable-next-line
            .catch((error) => {
                throw error
            })
    }
    
    /**
     * 編集
     */
    public async edit(no: string): Promise<User> {
        return await axios.get(`/api/user/edit/${no}`)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 新規保存
     */
    public async create(dto: User): Promise<User> {
        return await axios.post("/api/user/new", dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 保存
     */
    public async update(dto: User): Promise<User> {
        return await axios.put(`/api/user/edit/${dto.userId}`, dto)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 全件検索
     */
    public async findAll(): Promise<User[]> {
        return await axios.get("/api/users")
            .then((results) => {
                if (results.status === 200) {
                    return (results.data as User[]).map(x => new User({ ...x }))
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * ログインIDによる検索
     * @param userId 
     */
    public async findByUserId(userId: string): Promise<User> {
        return await axios.get(`/api/user/${userId}`)
            .then((results) => {
                if (results.status === 200) {
                    return new User({ ...results.data })
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 職種による検索
     * @param occupationCode(1: 事務員, 2:検査員)
     * @param isActive
     */
    public async findByOccupationCode(occupationCode: number, isActive = true): Promise<User[]> {
        const serchConditon = {occupationCode: occupationCode, isActive: isActive}
        return await axios.get("/api/users", {params: serchConditon})
            .then((results) => {
                if (results.status === 200) {
                    return (results.data as User[]).map(x => this.convert(x))
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 承認権限による検索
     * @param authorityCode(1: 見積書, 2:請書, 3:報告書, 4:請求書)
     */
    public async findByAuthorityCode(authorityCode: number): Promise<User[]> {
        return await axios.get(`/api/users/authority/${authorityCode}`)
            .then((results) => {
                if (results.status === 200) {
                    return (results.data as User[]).map(x => new User({ ...x }))
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * パスワード変更
     */
    public async changePassword(userId: string, request: ChangePassword): Promise<User> {
        return await axios.put(`/api/user/password/${userId}`, request)
            .then((results) => {
                if (results.status === 200) {
                    return this.convert(results.data)
                } else {
                    throw results.statusText
                }
            })
            .catch((error) => {
                throw error
            })
    }

    /**
     * 受信データの変換
     * @param data
     */
    // eslint-disable-next-line
    private convert(data: any): User {
        const user = data as User
        return new User({ ...user })
    }
}
